/**
 * Character Creation Modal - Quick 3-Field Create
 *
 * File: Mythbound.App/src/components/campaign/CharacterCreationModal.tsx
 * Created: 2026-01-06
 * Last Modified: 2026-09-21 (PRD001A 001A.4 — Character contract half)
 *
 * PURPOSE:
 * Modal dialog for quick character creation (name + level, optional class, HP and narrative).
 * Users can expand character sheet later for full details.
 *
 * WHO: Rendered from `CampaignPage.tsx`'s "Add Character" flow, opened via
 * `CharacterRosterSidebar.tsx`'s "Add Character" button.
 * WHAT: Calls `characterService.createQuickCharacter` DIRECTLY — this
 * component does NOT go through `useCharacter()`/`CharacterContext`. The
 * header comment here previously claimed a
 * "UI → useCharacter().createQuickCharacter → CharacterService" pipeline
 * that was never true (no `useCharacter` import ever existed in this file —
 * flagged in the 001A.1 inventory); corrected below to describe what the
 * code actually does.
 * WHY (PRD001A 001A.4; class optionality updated R2, 2026-09-23): request/
 * validation now mirror the FROZEN backend contract
 * (`characters_enhanced.py::CharacterQuickCreateRequest`,
 * `shared/contracts/character/v1/character-management.yaml`) exactly:
 * - `character_name`: required, trimmed server-side, 1-50 chars (not 20).
 * - `character_class`: GENUINELY OPTIONAL (R2) — the backend field is now
 *   `Optional[str] = Field(None, ...)`. Leaving the dropdown on its neutral
 *   placeholder omits `character_class` from the request entirely (never
 *   sent as `""`), which stores an unknown class — never a fabricated
 *   default and never a validation error either. Choosing a class from the
 *   list sends that exact value; the backend still 422s a value outside the
 *   recognized D&D 5e class list, though the dropdown itself only ever
 *   offers recognized values.
 * - `level`: required, integer 1-20.
 * - `hit_points` (NOT `hp` — the previous field name was wrong and would
 *   have been silently dropped/ignored by the backend's Pydantic model):
 *   optional; omitted leaves HP unknown, even when a class is provided.
 * - `duplicate_name` (409): the caller already has an active character in
 *   this campaign, OR the chosen name collides with another active
 *   character's name. Shown as a clear inline conflict message, not the
 *   generic "Failed to create character" fallback.
 *
 * [FUTURE CODESPACE]
 * - Character templates selection (Step 1: Choose template)
 * - Multi-step character creation wizard (Phase 2)
 * - Character import from D&D Beyond (Phase 2)
 *
 * Pipeline: UI → characterService.createQuickCharacter → Backend (no context involved)
 * Dependencies: React, characterService
 */

import React, { useState, useEffect } from 'react';
import { characterService, getCharacterErrorCode, type Character } from '../../services/api/CharacterService';

interface CharacterCreationModalProps {
  campaignId: string;
  isOpen: boolean;
  onClose: () => void;
  onCharacterCreated?: (character: Character) => void;
  /** Optional pre-selected class. Leave unset to start on the neutral
   * placeholder — do NOT default this to a real class value ("Fighter" etc.),
   * that is exactly the fabricated-default pattern this wave removes. */
  defaultClass?: string;
  defaultLevel?: number;
}

// D&D 5e class list — must match characters_enhanced.py's CharacterQuickCreateRequest enum exactly.
const DND5E_CLASSES = [
  'Barbarian',
  'Bard',
  'Cleric',
  'Druid',
  'Fighter',
  'Monk',
  'Paladin',
  'Ranger',
  'Rogue',
  'Sorcerer',
  'Warlock',
  'Wizard'
];

const NAME_MAX_LENGTH = 50;

export const CharacterCreationModal: React.FC<CharacterCreationModalProps> = ({
  campaignId,
  isOpen,
  onClose,
  onCharacterCreated,
  defaultClass = '',
  defaultLevel = 1
}) => {
  const [characterName, setCharacterName] = useState('');
  const [selectedClass, setSelectedClass] = useState(defaultClass);
  const [selectedLevel, setSelectedLevel] = useState(defaultLevel);
  const [hitPoints, setHitPoints] = useState<number | undefined>(undefined);
  const [characterDescription, setCharacterDescription] = useState('');
  const [preferenceNotes, setPreferenceNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [creationError, setCreationError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCharacterName('');
      setSelectedClass(defaultClass);
      setSelectedLevel(defaultLevel);
      setHitPoints(undefined);
      setCharacterDescription('');
      setPreferenceNotes('');
      setCreationError(null);
    }
  }, [isOpen, defaultClass, defaultLevel]);

  // Validation mirrors the frozen contract exactly:
  // name 1-50 trimmed, level integer 1-20. Class is genuinely optional (R2)
  // — an empty selection is a valid, deliberate "leave unknown" choice, not
  // a validation failure, so it is NOT part of isFormValid.
  const trimmedName = characterName.trim();
  // Count Unicode code points to match the server rule; maxLength can silently cut composed names.
  const isNameValid = Array.from(trimmedName).length >= 1 && Array.from(trimmedName).length <= NAME_MAX_LENGTH;
  const isLevelValid = Number.isInteger(selectedLevel) && selectedLevel >= 1 && selectedLevel <= 20;
  const isFormValid = isNameValid && isLevelValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) return;

    setIsSubmitting(true);
    setCreationError(null);

    try {
      // character_class is OMITTED from the request entirely when the
      // dropdown is still on its neutral placeholder ('') — never sent as
      // an empty string, which the backend would reject as an unrecognized
      // class rather than treat as "unknown." A chosen value is sent as-is.
      const character = await characterService.createQuickCharacter({
        campaign_id: campaignId,
        character_name: trimmedName,
        level: selectedLevel,
        ...(characterDescription.trim() ? { character_description: characterDescription.trim() } : {}),
        ...(preferenceNotes.trim() ? { character_preferences: { player_notes: preferenceNotes.trim() } } : {}),
        hit_points: hitPoints,
        ...(selectedClass ? { character_class: selectedClass } : {}),
      });

      // Success: notify parent and close modal
      if (onCharacterCreated) {
        onCharacterCreated(character);
      }

      // Reset form
      setCharacterName('');
      setSelectedClass(defaultClass);
      setSelectedLevel(defaultLevel);
      setHitPoints(undefined);
      setCharacterDescription('');
      setPreferenceNotes('');

      onClose();

    } catch (error) {
      // duplicate_name (409) gets its own clear inline message; every other
      // failure (validation/forbidden/not_found/unavailable/network) falls
      // back to the error's own message, which ApiError already derives
      // from the contract's {"detail": {"message", "code"}} body.
      const code = getCharacterErrorCode(error);
      if (code === 'duplicate_name') {
        setCreationError('You already have an active character with that name in this campaign. Choose a different name.');
      } else {
        setCreationError(error instanceof Error ? error.message : 'Failed to create character. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        className="character-creation-modal parchment-bg"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(to bottom, #f4e4c1, #e8d4a8)',
          border: '3px solid #d4af37',
          borderRadius: '12px',
          padding: '32px',
          minWidth: '400px',
          maxWidth: '500px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          position: 'relative'
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{
            margin: 0,
            fontSize: '24px',
            color: '#2c1810',
            fontWeight: 'bold',
            textAlign: 'center',
            fontFamily: 'Georgia, serif'
          }}>
            Create New Character
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Character Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              color: '#2c1810',
              fontWeight: '600'
            }}>
              Character Name <span style={{ color: '#c41e3a' }}>*</span>
            </label>
            <input
              type="text"
              value={characterName}
              onChange={(e) => setCharacterName(e.target.value)}
              placeholder="Enter character name"
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '15px',
                border: '2px solid #8b7355',
                borderRadius: '6px',
                backgroundColor: '#fff',
                color: '#2c1810',
                boxSizing: 'border-box'
              }}
            />
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '4px'
            }}>
              {Array.from(characterName).length}/{NAME_MAX_LENGTH} characters
            </div>
          </div>

          {/* Optional narrative is sent only when the player supplied text. */}
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="character-description" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#2c1810', fontWeight: '600' }}>Backstory <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span></label>
            <textarea id="character-description" value={characterDescription} onChange={(e) => setCharacterDescription(e.target.value)} rows={3} style={{ width: '100%', padding: '10px 12px', border: '2px solid #8b7355', borderRadius: '6px', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="character-preferences" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#2c1810', fontWeight: '600' }}>Play preferences <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span></label>
            <textarea id="character-preferences" value={preferenceNotes} onChange={(e) => setPreferenceNotes(e.target.value)} rows={2} placeholder="How would you like to play this character?" style={{ width: '100%', padding: '10px 12px', border: '2px solid #8b7355', borderRadius: '6px', boxSizing: 'border-box' }} />
          </div>
          {/* Character Class */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              color: '#2c1810',
              fontWeight: '600'
            }}>
              Class <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span>
            </label>
            {/* No pre-selected value, and the placeholder is selectable (not
                `disabled`) — leaving/returning to it is a deliberate "leave
                class unknown" choice (R2: class is genuinely optional), not
                an incomplete form. A fabricated client-side default
                ("Fighter") would misrepresent an un-made choice as one. */}
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '15px',
                border: '2px solid #8b7355',
                borderRadius: '6px',
                backgroundColor: '#fff',
                color: '#2c1810',
                boxSizing: 'border-box',
                cursor: 'pointer'
              }}
            >
              <option value="">
                No class (unknown for now)
              </option>
              {DND5E_CLASSES.map((cls) => (
                <option key={cls} value={cls}>
                  {cls}
                </option>
              ))}
            </select>
          </div>

          {/* Level */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              color: '#2c1810',
              fontWeight: '600'
            }}>
              Level <span style={{ color: '#c41e3a' }}>*</span>
            </label>
            <input
              type="number"
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(Number(e.target.value))}
              min={1}
              max={20}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '15px',
                border: '2px solid #8b7355',
                borderRadius: '6px',
                backgroundColor: '#fff',
                color: '#2c1810',
                boxSizing: 'border-box'
              }}
            />
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '4px'
            }}>
              Level 1-20
            </div>
          </div>

          {/* HP (Optional) */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              color: '#2c1810',
              fontWeight: '600'
            }}>
              Hit Points (Optional)
            </label>
            <input
              type="number"
              value={hitPoints || ''}
              onChange={(e) => setHitPoints(e.target.value ? Number(e.target.value) : undefined)}
              placeholder="Leave blank: HP remains unknown"
              min={1}
              max={999}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '15px',
                border: '2px solid #8b7355',
                borderRadius: '6px',
                backgroundColor: '#fff',
                color: '#2c1810',
                boxSizing: 'border-box'
              }}
            />
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '4px'
            }}>
              Leave blank for automatic calculation based on class
            </div>
          </div>

          {/* Error Message */}
          {creationError && (
            <div style={{
              padding: '12px',
              backgroundColor: '#ffe0e0',
              border: '1px solid #c41e3a',
              borderRadius: '6px',
              marginBottom: '20px',
              fontSize: '14px',
              color: '#c41e3a'
            }}>
              {creationError}
            </div>
          )}

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: '10px 20px',
                fontSize: '15px',
                fontWeight: '600',
                border: '2px solid #8b7355',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                color: '#2c1810',
                cursor: 'pointer',
                transition: 'all 0.2s',
                opacity: isSubmitting ? 0.5 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              style={{
                padding: '10px 24px',
                fontSize: '15px',
                fontWeight: '600',
                border: '2px solid #d4af37',
                borderRadius: '6px',
                background: isFormValid && !isSubmitting
                  ? 'linear-gradient(to bottom, #f9d423, #e0a819)'
                  : '#ccc',
                color: '#2c1810',
                cursor: isFormValid && !isSubmitting ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
                boxShadow: isFormValid && !isSubmitting ? '0 2px 8px rgba(212, 175, 55, 0.4)' : 'none'
              }}
            >
              {isSubmitting ? 'Creating...' : 'Create Character'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
